import { MapBuilder, px, type Decor, type DecorKind, type Light, type MapDef, type Rect } from '../mapdef';

/**
 * Taipei (xinyi package): one walkable slice of the capital, west to east.
 *
 *   north band  : Taipei Main Station (green-tiled roof) · Sun Yat-sen Memorial Hall + Cuihu pond · Taipei 101 + plaza
 *   avenue      : 忠孝東路 / 信義路 across the whole map, Keelung Road crossing north–south
 *   south band  : 騎樓 shop arcades, side street, MRT entrance, food stalls, shrine and park · Four Four South Village
 *                 + Sunday Simple Market · Xinyi department stores, pedestrian plaza and the skybridges
 *   east        : Elephant Mountain (象山): stone stairs, the Six Giant Rocks and the viewpoint over 101
 *
 * Tile chars owned here: '1' stone stairs / trail decks, '2' Xinyi plaza paving, '3' solid (building footprints,
 * forested hillside on the mountain).
 */
const W = 100, H = 60;
const b = new MapBuilder(W, H, 's');

// --- Backdrop and the mountain ---------------------------------------------------------------------
b.fill(0, 0, 85, 3, 'O').fill(0, 0, 2, H, 'O').fill(0, H - 2, 85, 2, 'O');
b.fill(85, 0, 15, H, '3').fill(84, 3, 1, 10, '3');

// --- North band: Taipei Main Station (x 2–29) -------------------------------------------------------
b.fill(2, 3, 28, 2, 'O').fill(2, 5, 28, 8, '3');
b.fill(2, 13, 28, 9, 'X');
b.fill(3, 18, 22, 2, 'r').fill(14, 18, 4, 2, '=');

// Keelung Road, north to south, with zebra crossings.
b.fill(30, 3, 4, 55, 'r').fill(30, 15, 4, 2, '=').fill(30, 34, 4, 2, '=').fill(30, 44, 4, 2, '=');

// --- North band: Sun Yat-sen Memorial Hall (x 34–62) -----------------------------------------------
b.fill(34, 3, 29, 9, 'g').fill(39, 4, 21, 8, '3').fill(34, 3, 29, 1, 'h');
b.fill(34, 12, 29, 10, 'X');
b.fill(34, 13, 7, 9, 'g').fill(35, 14, 5, 6, '~');

// --- North band: Taipei 101 (x 63–84) --------------------------------------------------------------
b.fill(63, 3, 21, 10, '2');
b.building(66, 3, 18, 10, { roof: 'O', wall: 'J', window: 'J', roofRows: 6, doors: [], windows: [] });
b.fill(63, 13, 22, 9, '2');

// --- The avenue ------------------------------------------------------------------------------------
b.fill(0, 22, 85, 1, 'Q').fill(0, 23, 85, 2, 'r').fill(0, 25, 85, 1, 'l').fill(0, 26, 85, 2, 'r').fill(0, 28, 85, 1, 'Q');
b.fill(14, 23, 4, 5, '=').fill(46, 23, 4, 5, '=').fill(70, 23, 4, 5, '=');
b.fill(30, 22, 4, 7, 'r');

// --- South-west: arcades, side street, MRT, stalls, shrine, park (x 2–29) ---------------------------
b.fill(2, 29, 28, 4, '3').fill(2, 33, 28, 1, 'z').fill(2, 34, 28, 1, 'Q');
b.fill(2, 37, 28, 3, 'r');
b.fill(2, 41, 28, 1, 'Q');
b.building(4, 43, 8, 5, { roof: 'O', wall: 'J', window: 'J', roofRows: 3, doors: [3, 4], windows: [] });
b.fill(2, 42, 28, 1, 's').fill(13, 43, 17, 6, 'n');
b.fill(2, 49, 28, 8, 'g').fill(2, 52, 28, 1, 'p').fill(15, 49, 2, 8, 'p').fill(5, 50, 6, 2, 'G').fill(21, 54, 6, 2, 'G');

// --- South-middle: Four Four South Village (x 34–59) -----------------------------------------------
b.fill(34, 29, 26, 29, 's');
for (const hx of [35, 41, 47, 53]) {
  b.fill(hx, 31, 5, 2, '3');
  b.fill(hx, 37, 5, 2, '3');
}
for (const hx of [35, 41]) b.fill(hx, 43, 5, 2, '3');
b.fill(46, 41, 13, 9, 'g').fill(46, 45, 13, 1, 'p').fill(35, 35, 24, 1, 'g');
b.fill(34, 51, 26, 7, 'g').fill(34, 53, 26, 1, 'p').fill(46, 50, 2, 8, 'p');

// --- South-east: Xinyi department stores and the pedestrian plaza (x 60–84) -------------------------
b.fill(60, 29, 25, 29, '2');
b.fill(60, 30, 8, 6, '3').fill(69, 30, 6, 6, '3').fill(76, 30, 5, 6, '3');
b.fill(61, 42, 2, 3, '3');
b.fill(62, 52, 7, 4, 'g').fill(74, 52, 7, 4, 'g');

// --- Elephant Mountain: stone stairs, the Six Giant Rocks, the viewpoint ----------------------------
b.fill(83, 50, 6, 2, '1');
b.fill(87, 37, 2, 15, '1');
b.fill(87, 36, 7, 2, '1');
b.fill(92, 26, 2, 11, '1');
b.fill(88, 19, 10, 8, 'g');
b.fill(89, 10, 2, 10, '1');
b.fill(85, 4, 6, 7, '1');

// ----------------------------------------------------------------------------------------------------
// Decor helpers
// ----------------------------------------------------------------------------------------------------
const at = (tx: number, ty: number) => ({ x: px(tx), y: px(ty) });
const tree = (tx: number, ty: number, kind: 'tree' | 'cherry' | 'pine' = 'tree'): Decor => ({ kind, ...at(tx, ty), solid: { x: px(tx) - 5, y: px(ty) - 5, w: 10, h: 5 } });
const lamp = (tx: number, ty: number): Decor => ({ kind: 'lamp', ...at(tx, ty), solid: { x: px(tx) - 2, y: px(ty) - 3, w: 4, h: 3 } });
const solidBox = (x: number, y: number, w: number, h: number): Rect => ({ x, y, w, h });

/**
 * Big landmarks are split into horizontal slices 90 px tall (the renderer culls a decor by its bottom and
 * `y - 90`), so the top of a tall building never pops out while its foot is off screen.
 * `variant` = style * 10 + slice index; the drawer rebuilds the bottom from it.
 */
function tall(kind: DecorKind, x: number, bottom: number, height: number, w: number, style = 0, solid?: Rect): Decor[] {
  const n = Math.max(1, Math.ceil((height - 2) / 90));
  return Array.from({ length: n }, (_, i): Decor => ({ kind, x, y: bottom - 90 * i, w, variant: style * 10 + i, ...(i === 0 && solid ? { solid } : {}) }));
}

const decor: Decor[] = [];
const add = (...d: Decor[]) => decor.push(...d);

// --- Taipei Main Station ---------------------------------------------------------------------------
add(...tall('xy_station', px(15) + 8, px(13), 136, px(26)));
add({ kind: 'stationSign', x: px(6), y: px(16), variant: 1, solid: solidBox(px(6) - 22, px(16) - 4, 44, 4) });
add(lamp(3, 14), lamp(11, 14), lamp(20, 14), lamp(28, 14), lamp(3, 21), lamp(28, 21));
add({ kind: 'planter', ...at(9, 17), solid: solidBox(px(9) - 14, px(17) - 8, 28, 8) });
add({ kind: 'planter', ...at(20, 17), solid: solidBox(px(20) - 14, px(17) - 8, 28, 8) });
add({ kind: 'xy_infoBooth', x: px(26) + 8, y: px(15), solid: solidBox(px(25) + 2, px(15) - 8, 28, 8) });
add({ kind: 'flowerbed', ...at(4, 17) }, { kind: 'flowerbed', ...at(28, 17) });
// Yellow taxis waiting in the drop-off lane.
for (const [tx, v] of [[4, 0], [7, 1], [10, 0], [19, 1], [22, 0]] as const) add({ kind: 'xy_taxi', x: px(tx) + 8, y: px(20) - 2, variant: v, solid: solidBox(px(tx) - 5, px(19) + 2, 26, 10) });
add({ kind: 'xy_scooterRow', x: px(26), y: px(21) + 6, w: px(4), variant: 1 });

// --- Sun Yat-sen Memorial Hall ---------------------------------------------------------------------
add(...tall('xy_sysHall', px(49) + 8, px(12), 158, px(22)));
add({ kind: 'xy_guard', x: px(46) + 8, y: px(13) + 2, solid: solidBox(px(46) + 4, px(13) - 2, 8, 4) });
add({ kind: 'xy_guard', x: px(52) + 8, y: px(13) + 2, solid: solidBox(px(52) + 4, px(13) - 2, 8, 4) });
for (const tx of [35, 37, 61, 59]) add(tree(tx, 6, 'pine'), tree(tx, 10, 'pine'));
add(tree(41, 21, 'pine'), tree(57, 21, 'pine'), tree(61, 15, 'pine'), tree(62, 21, 'pine'));
add(tree(34, 21), tree(40, 14, 'cherry'));
add(lamp(44, 21), lamp(54, 21), lamp(44, 15), lamp(54, 15));
add({ kind: 'xy_lotus', x: px(37) + 8, y: px(17), w: px(5) });
add({ kind: 'xy_bench', ...at(42, 18) }, { kind: 'xy_bench', ...at(56, 18) });
add({ kind: 'flowerbed', ...at(46, 16) }, { kind: 'flowerbed', ...at(53, 16) }, { kind: 'flowerbed', ...at(46, 20) }, { kind: 'flowerbed', ...at(53, 20) });
add({ kind: 'xy_pigeons', x: px(51), y: px(18), variant: 1 }, { kind: 'xy_pigeons', x: px(10), y: px(15), variant: 2 }, { kind: 'xy_pigeons', x: px(76), y: px(46), variant: 3 });

// --- Taipei 101 ------------------------------------------------------------------------------------
add({ kind: 'tower101', x: px(75), y: px(9) });
add({ kind: 'sign101', ...at(78, 17), solid: solidBox(px(78) - 26, px(17) - 6, 52, 6) });
add({ kind: 'planter', ...at(65, 15), solid: solidBox(px(65) - 14, px(15) - 8, 28, 8) });
add({ kind: 'planter', ...at(84, 15) });
add(tree(64, 20), tree(68, 21, 'cherry'), lamp(73, 21), lamp(63, 13));
add({ kind: 'xy_scooterRow', x: px(76), y: px(21) + 6, w: px(4), variant: 2 });

// --- The avenue ------------------------------------------------------------------------------------
add({ kind: 'trafficLight', ...at(13, 22) }, { kind: 'trafficLight', ...at(18, 28) });
add({ kind: 'trafficLight', ...at(45, 22) }, { kind: 'trafficLight', ...at(50, 28) });
add({ kind: 'trafficLight', ...at(69, 22) }, { kind: 'trafficLight', ...at(74, 28) });
// Scooters waiting at the lights (the famous scooter waves).
add({ kind: 'xy_scooterWave', x: px(10), y: px(27) - 2, w: px(4), variant: 0 });
add({ kind: 'xy_scooterWave', x: px(42), y: px(24) - 2, w: px(4), variant: 1 });
add({ kind: 'xy_scooterWave', x: px(66), y: px(27) - 2, w: px(4), variant: 2 });
add({ kind: 'xy_bus', x: px(56), y: px(24) + 2 });
add({ kind: 'xy_taxi', x: px(25), y: px(27) - 1, variant: 1 });
add({ kind: 'xy_taxi', x: px(80), y: px(24), variant: 0 });
add(lamp(6, 22), lamp(24, 22), lamp(38, 22), lamp(58, 22), lamp(6, 28), lamp(24, 28), lamp(38, 28), lamp(58, 28), lamp(80, 28));

// --- South-west: shop arcades (騎樓) -----------------------------------------------------------------
// The shopfronts at the back of the arcade, then the pillars and the upper floors with their signboards.
add({ kind: 'xy_shopBack', x: px(16), y: px(33), w: px(28), variant: 0 });
add(...tall('xy_arcade', px(16), px(34), 84, px(28), 0));
add({ kind: 'xy_scooterRow', x: px(6), y: px(36) + 4, w: px(8), variant: 0 });
add({ kind: 'xy_scooterRow', x: px(18), y: px(36) + 4, w: px(9), variant: 3 });
add({ kind: 'xy_scooterRow', x: px(10), y: px(41) + 4, w: px(6), variant: 4 });
add(lamp(15, 36), lamp(29, 36), lamp(3, 41), lamp(29, 41));
add({ kind: 'xy_garbageTruck', x: px(22), y: px(39) + 4, w: px(5) });
add({ kind: 'mrtSign', ...at(8, 49) });
add({ kind: 'xy_vending', x: px(12) + 8, y: px(46), solid: solidBox(px(12), px(46) - 6, 16, 6) });
add({ kind: 'lanternString', x: px(13), y: px(43) + 4, w: px(10) });
add({ kind: 'xy_banyan', x: px(28), y: px(46) - 2, solid: solidBox(px(27) + 6, px(46) - 8, 20, 6) });
add({ kind: 'trashBin', ...at(18, 48) });
add(tree(3, 50), tree(8, 55, 'cherry'), tree(12, 50), tree(19, 51), tree(27, 50), tree(24, 56), tree(4, 56));
add({ kind: 'xy_bench', ...at(9, 54) }, { kind: 'xy_bench', ...at(20, 54) });
add(lamp(14, 53), lamp(17, 49), lamp(29, 53));

// --- Four Four South Village (四四南村) ----------------------------------------------------------------
[35, 41, 47, 53].forEach((hx, i) => add({ kind: 'xy_juancun', x: px(hx) + 40, y: px(33), w: px(5), variant: i % 3, solid: solidBox(px(hx), px(31), px(5), px(2)) }));
[35, 41, 47, 53].forEach((hx, i) => add({ kind: 'xy_juancun', x: px(hx) + 40, y: px(39), w: px(5), variant: i === 2 ? 9 : (i + 1) % 3, solid: solidBox(px(hx), px(37), px(5), px(2)) }));
[35, 41].forEach((hx, i) => add({ kind: 'xy_juancun', x: px(hx) + 40, y: px(45), w: px(5), variant: (i + 2) % 3, solid: solidBox(px(hx), px(43), px(5), px(2)) }));
add({ kind: 'xy_cafeTables', x: px(50), y: px(41) - 2 });
add({ kind: 'xy_bicycle', x: px(39) + 4, y: px(33) + 10, variant: 0 }, { kind: 'xy_bicycle', x: px(57), y: px(39) + 10, variant: 1 }, { kind: 'xy_bicycle', x: px(45), y: px(45) + 10, variant: 2 });
add({ kind: 'xy_laundry', x: px(40) + 8, y: px(42), w: 14 }, { kind: 'xy_laundry', x: px(52) + 8, y: px(36), w: 14 });
add(tree(58, 35), tree(59, 41, 'cherry'), tree(58, 44), tree(59, 49, 'cherry'), tree(36, 49));
add({ kind: 'xy_marketSign', x: px(57), y: px(42) });
add(lamp(40, 34), lamp(52, 34), lamp(46, 46), lamp(34, 47));
add(tree(35, 55), tree(40, 52), tree(44, 56, 'cherry'), tree(51, 52), tree(57, 56));
add({ kind: 'xy_bench', ...at(38, 54) }, { kind: 'xy_bench', ...at(54, 54) });

// --- Xinyi department stores, plaza and skybridges ---------------------------------------------------
add(...tall('xy_dept', px(64), px(36), 116, px(8), 0, solidBox(px(60), px(30), px(8), px(6))));
add(...tall('xy_dept', px(72), px(36), 108, px(6), 1, solidBox(px(69), px(30), px(6), px(6))));
add(...tall('xy_dept', px(78) + 8, px(36), 112, px(5), 2, solidBox(px(76), px(30), px(5), px(6))));
// N–S skybridge from the 101 podium, over the avenue, down to the plaza; then E–W to the MRT exit.
for (let ty = 13; ty <= 43; ty++) add({ kind: 'xy_skyNS', x: px(83), y: px(ty + 1), variant: ty === 13 ? 2 : 0 });
for (let tx = 63; tx < 82; tx += 2) add({ kind: 'xy_skyEW', x: px(tx) + 16, y: px(44), w: 32, variant: [67, 73, 79].includes(tx) ? 1 : 0, ...([67, 73, 79].includes(tx) ? { solid: solidBox(px(tx) + 13, px(44) - 4, 6, 4) } : {}) });
add({ kind: 'xy_skyStairs', x: px(62), y: px(45), solid: solidBox(px(61), px(42), px(2), px(3)) });
add(tree(61, 39), tree(68, 46), tree(75, 39, 'cherry'), tree(80, 47), tree(66, 53), tree(77, 53), tree(71, 57), tree(62, 57));
add({ kind: 'xy_bench', ...at(70, 41) }, { kind: 'xy_bench', ...at(78, 41) }, { kind: 'xy_bench', ...at(71, 50) });
add(lamp(64, 41), lamp(74, 46), lamp(81, 41), lamp(68, 50), lamp(81, 55), lamp(60, 50));
add({ kind: 'xy_art', x: px(77), y: px(49), solid: solidBox(px(77) - 12, px(49) - 6, 24, 6) });
add({ kind: 'flowerbed', ...at(65, 49) }, { kind: 'flowerbed', ...at(71, 54) }, { kind: 'flowerbed', ...at(66, 57) });
add({ kind: 'xy_scooterRow', x: px(61), y: px(56) + 4, w: px(2), variant: 6 });
add({ kind: 'xy_scooterRow', x: px(62), y: px(29) + 12, w: px(4), variant: 1 }, { kind: 'xy_scooterRow', x: px(80) + 8, y: px(29) + 12, w: px(3), variant: 5 });

// --- Elephant Mountain ------------------------------------------------------------------------------
// Forest edge along the foot of the hill and on the slopes beside the stairs.
for (const [tx, ty] of [[85, 14], [85, 18], [85, 22], [85, 30], [85, 35], [85, 46], [85, 55], [84, 58], [86, 50], [90, 45], [86, 38], [90, 41], [91, 33], [94, 39], [88, 17], [92, 14], [92, 8], [87, 12], [97, 18], [98, 27], [90, 28], [95, 44], [98, 50], [92, 54], [97, 36]] as const) {
  add(tree(tx, ty, (tx + ty) % 3 === 0 ? 'tree' : 'pine'));
}
add({ kind: 'xy_rock', x: px(89) + 8, y: px(22), variant: 0, solid: solidBox(px(89) - 4, px(22) - 8, 24, 8) });
add({ kind: 'xy_rock', x: px(91) + 8, y: px(20), variant: 1, solid: solidBox(px(91) - 2, px(20) - 6, 20, 6) });
add({ kind: 'xy_rock', x: px(95), y: px(21), variant: 2, solid: solidBox(px(94) - 4, px(21) - 8, 24, 8) });
add({ kind: 'xy_rock', x: px(97), y: px(24), variant: 3, solid: solidBox(px(96), px(24) - 8, 16, 8) });
add({ kind: 'xy_rock', x: px(89) + 8, y: px(26), variant: 4, solid: solidBox(px(89), px(26) - 6, 16, 6) });
add({ kind: 'xy_rock', x: px(96), y: px(27), variant: 5, solid: solidBox(px(95) + 4, px(27) - 6, 18, 6) });
add({ kind: 'xy_railing', x: px(85), y: px(11), h: px(7) });
add({ kind: 'xy_railing', x: px(91), y: px(11), h: px(7) });
add({ kind: 'xy_stepLamp', ...at(88, 44) }, { kind: 'xy_stepLamp', ...at(91, 36) }, { kind: 'xy_stepLamp', ...at(94, 30) }, { kind: 'xy_stepLamp', ...at(91, 14) }, { kind: 'xy_stepLamp', ...at(91, 8) });

// ====================================================================================================
// Street life pass: crowds, queues, vehicles, signs, cables, trees, furniture, cats... (src/render/streetLife.ts)
// ====================================================================================================
type Dir = 0 | 1 | 2 | 3;
/** A crowd of `n` people spread over w × h; mode: 0 mixed, 1 shoppers, 2 tourists, 3 travellers, 4 hikers, 5 facing up, 6 facing down. */
const crowd = (tx: number, ty: number, n: number, w: number, h: number, mode = 0, seed = 0): Decor => ({ kind: 'xy_crowd', x: px(tx), y: px(ty), w, h, variant: n + 16 * seed + 256 * mode });
/** One person (look 0-31, facing 0 down/1 up/2 left/3 right, accessory: 1 bags, 2 parasol, 3 suitcase, 4 phone, 5 bubble tea, 6 hiker, 7 balloon, 8 camera). */
const one = (tx: number, ty: number, look: number, facing: Dir, acc = 0, solid = true): Decor => ({ kind: 'xy_person', x: px(tx), y: px(ty), variant: acc * 128 + (look % 32) * 4 + facing, ...(solid ? { solid: solidBox(px(tx) - 4, px(ty) - 3, 8, 3) } : {}) });
/** A queue whose head stands at the anchor: dir 0 faces right (extends west), 1 faces left (extends east), 2 faces up (extends south). */
const queue = (tx: number, ty: number, n: number, dir: 0 | 1 | 2, seed = 0): Decor => ({ kind: 'xy_queue', x: px(tx), y: px(ty), variant: n + 16 * seed + 256 * dir });
const benchSit = (tx: number, ty: number, seed = 0, solid = true): Decor => ({ kind: 'xy_benchSit', x: px(tx), y: px(ty), w: 30, variant: seed, ...(solid ? { solid: solidBox(px(tx) - 15, px(ty) - 10, 30, 10) } : {}) });
const streetTree = (tx: number, ty: number, v = 0): Decor => ({ kind: 'xy_streetTree', x: px(tx), y: px(ty), variant: v, solid: solidBox(px(tx) - 5, px(ty) - 4, 10, 4) });
const pole = (tx: number, ty: number, w = 128, v = 0): Decor => ({ kind: 'xy_cables', x: px(tx), y: px(ty), w, variant: v, ...(v === 0 ? { solid: solidBox(px(tx) - 2, px(ty) - 3, 4, 3) } : {}) });
const vsign = (tx: number, ty: number, v: number): Decor => ({ kind: 'xy_vsign', x: px(tx), y: px(ty), variant: v });
const foodTruck = (tx: number, ty: number, v: number): Decor => ({ kind: 'xy_foodTruck', x: px(tx), y: px(ty), w: 60, variant: v, solid: solidBox(px(tx) - 27, px(ty) - 10, 54, 10) });
const parterre = (tx: number, ty: number, w: number, h: number, v = 0): Decor => ({ kind: 'xy_parterre', x: px(tx), y: px(ty), w, h, variant: v, solid: solidBox(px(tx) - w / 2 - 2, px(ty) - h - 2, w + 4, h + 2) });
const cat = (tx: number, ty: number, v: number, dir = 1, h = 0): Decor => ({ kind: 'xy_cat', x: px(tx), y: px(ty), w: dir, h, variant: v });
const ubike = (tx: number, ty: number, w: number): Decor => ({ kind: 'xy_ubikeDock', x: px(tx), y: px(ty), w, solid: solidBox(px(tx) - w / 2 - 8, px(ty) - 6, w + 8, 6) });
const tinShed = (tx: number, ty: number, w: number, v: number): Decor => ({ kind: 'xy_tinShed', x: px(tx), y: px(ty), w, variant: v, solid: solidBox(px(tx) - w / 2, px(ty) - 8, w, 8) });
const ground = (kind: 'xy_manhole' | 'xy_laneArrow' | 'xy_puddle' | 'xy_leaves', tx: number, ty: number, v = 0, w?: number): Decor => ({ kind, x: px(tx), y: px(ty), variant: v, ...(w ? { w } : {}) });
const walkers = (tx: number, ty: number, w: number, vertical: boolean, seed = 0): Decor => ({ kind: 'xy_walkers', x: px(tx), y: px(ty), w, variant: seed + (vertical ? 16 : 0) });

// --- Taipei Main Station: the forecourt is where the whole city changes trains ------------------------
add(crowd(12, 14.6, 5, 56, 14, 3, 1), crowd(22, 15.2, 4, 30, 10, 0, 2), crowd(7, 15.4, 3, 28, 6, 2, 3), crowd(17, 20.8, 4, 40, 6, 3, 4));
add(one(10.2, 16.3, 5, 3, 3), one(18.6, 16.4, 21, 0, 2), one(3.4, 16.2, 9, 0, 5), one(27, 20.6, 17, 1, 0, false), one(13.6, 20.7, 30, 2, 3, false), one(8.8, 20.9, 2, 3, 1, false));
// The taxi queue, waiting beside the rank (head of the queue by the first cab).
add(queue(15.2, 17.4, 4, 0, 2));
add(benchSit(7.2, 16.9, 1), benchSit(23, 16.9, 2));
add({ kind: 'trashBin', ...at(10.4, 16.6) }, { kind: 'trashBin', ...at(18.2, 16.6) });
add({ kind: 'xy_pigeons2', x: px(14.5), y: px(16.3), w: 30, variant: 1 }, { kind: 'xy_pigeons2', x: px(25), y: px(14), w: 24, variant: 2 });
add(streetTree(6.5, 21.6, 0), streetTree(16, 21.6, 1), streetTree(23.5, 21.6, 0));
add({ kind: 'xy_bus', x: px(15), y: px(19) - 2, variant: 1 });
add(ground('xy_manhole', 12, 20.6), ground('xy_manhole', 21, 19.6), ground('xy_leaves', 16.5, 21.3, 1, 36), ground('xy_leaves', 7, 21.2, 2, 30));
// Zebra-crossing crowds on Keelung Road and across the avenue.
add(walkers(31.9, 16.5, 64, false, 1), walkers(31.9, 35.5, 64, false, 2), walkers(31.9, 45.5, 64, false, 3));
add(walkers(15.5, 28, 80, true, 4), walkers(47.5, 28, 80, true, 5), walkers(71.5, 28, 80, true, 6));

// --- The avenue: poles and cables, more buses, lane paint, pedestrians on the pavements -------------
add(pole(2.5, 22.9, 128), pole(10.5, 22.9, 128), pole(18.5, 22.9, 128), pole(26.5, 22.9, 56));
add(pole(36, 28.9, 128), pole(44, 28.9, 128), pole(52, 28.9, 112), pole(59, 28.9, 0, 0));
add({ kind: 'xy_bus', x: px(18), y: px(27) + 1, variant: 0 }, { kind: 'xy_bus', x: px(76), y: px(24) + 2, variant: 1 });
add({ kind: 'xy_taxi', x: px(36), y: px(27) - 1, variant: 1 }, { kind: 'xy_taxi', x: px(5), y: px(24), variant: 0 });
add(ground('xy_laneArrow', 6, 24.8), ground('xy_laneArrow', 40, 24.8), ground('xy_laneArrow', 62, 24.8, 1), ground('xy_laneArrow', 27, 27.8), ground('xy_laneArrow', 56, 27.8), ground('xy_laneArrow', 80, 27.8, 1));
add(ground('xy_manhole', 9, 25.6), ground('xy_manhole', 41, 25.6), ground('xy_manhole', 64, 25.6), ground('xy_puddle', 22, 26.7, 0, 20), ground('xy_puddle', 61, 23.7, 0, 16));
add(one(8, 22.9, 4, 3, 1, false), one(21, 22.9, 11, 2, 0, false), one(40, 22.9, 19, 3, 2, false), one(55, 22.9, 26, 0, 5, false), one(66, 22.9, 7, 1, 0, false));
add(one(9, 28.9, 14, 2, 1, false), one(28, 28.9, 23, 3, 0, false), one(41, 28.9, 3, 3, 3, false), one(63, 28.9, 29, 0, 1, false), one(77, 28.9, 16, 2, 2, false));

// --- Sun Yat-sen Memorial Hall: tourists, kites, parterres, the evening dancers ----------------------
add(crowd(49, 17.4, 5, 50, 10, 2, 5), crowd(44, 19.6, 3, 26, 6, 0, 6), one(55.5, 19.4, 12, 1, 4), one(57, 15.4, 1, 0, 2));
add(parterre(44, 21.4, 48, 14, 0), parterre(55, 21.4, 40, 14, 2));
add({ kind: 'ck_kites', x: px(38), y: px(20), w: 100 });
add({ kind: 'ck_pigeons', x: px(50), y: px(19.6), w: 50, variant: 3 });
add({ kind: 'ck_dancers', x: px(49), y: px(19.6), w: 120 });
add(benchSit(36, 21.4, 3), ground('xy_leaves', 36, 17.6, 3, 30), ground('xy_leaves', 60, 18.4, 4, 30));

// --- Taipei 101: the queue for the observatory, selfies, an ice-cream truck ------------------------
add(queue(75, 16.5, 5, 2, 6), crowd(75, 19.4, 6, 56, 10, 2, 7), crowd(66, 18.6, 3, 24, 6, 1, 8));
add(one(81, 18.4, 27, 1, 4), one(63.8, 17.2, 6, 0, 1), one(77.5, 14.8, 15, 1, 8, false));
add(benchSit(65, 18.9, 4), foodTruck(80, 20.7, 2), queue(77.2, 20.6, 2, 1, 9));
add(streetTree(67, 15.8, 0), streetTree(82, 16, 0));
add({ kind: 'trashBin', ...at(70, 15.6) }, { kind: 'xy_pigeons2', x: px(72), y: px(20.4), w: 26, variant: 4 });

// --- The arcades: vertical signs, people at the shops, scooters, cables, a taxi ----------------------
add(vsign(4.2, 34.5, 0), vsign(7.6, 34.5, 2), vsign(13.3, 34.5, 4), vsign(17.4, 34.5, 10), vsign(21.2, 34.5, 1), vsign(25.6, 34.5, 8), vsign(28.6, 34.5, 6));
add(crowd(7, 33.7, 3, 36, 4, 1, 10), crowd(19.5, 33.7, 3, 30, 4, 0, 11), one(14.5, 33.8, 24, 1, 5, false), one(26.5, 33.8, 8, 0, 1, false));
add(queue(10.4, 34.6, 3, 1, 12));
add({ kind: 'xy_scooterRow', x: px(26), y: px(41) + 4, w: px(4), variant: 7 });
add({ kind: 'xy_taxi', x: px(8), y: px(38) + 6, variant: 0 });
add(pole(3, 41.9, 128), pole(11, 41.9, 128), pole(19, 41.9, 128), pole(27, 41.9, 0, 0));
add(ground('xy_manhole', 16, 38.6), ground('xy_puddle', 5, 39.7, 0, 18), ground('xy_laneArrow', 18, 39.8));
add(one(24.5, 41.9, 18, 3, 0, false), one(6, 41.9, 31, 2, 1, false));

// --- Station front: MRT exit crowd, the market, UBikes, a cat, the park ------------------------------
add(crowd(12, 48.6, 3, 24, 6, 3, 13), crowd(22, 46.2, 4, 44, 10, 1, 14), one(26, 47.4, 10, 2, 5), one(17.4, 44.3, 20, 1, 0, false));
add(ubike(24.5, 42.9, 56), cat(27.6, 48.6, 0, -1), { kind: 'xy_pigeons2', x: px(12.5), y: px(53.4), w: 26, variant: 5 });
add({ kind: 'xy_stroller', x: px(18), y: px(52.7), w: 1, variant: 2 });
add(parterre(23.5, 55.7, 64, 18, 1), parterre(7.5, 51.6, 56, 14, 3));
add({ kind: 'ck_kites', x: px(24), y: px(54) + 4, w: 100, variant: 1 });
add(cat(5.5, 53.6, 1, 1), ground('xy_leaves', 12, 50.8, 5, 34), ground('xy_leaves', 26, 55.6, 6, 30));

// --- Four Four South Village: washing, sheds, pot plants, cats, elders chatting ----------------------
add({ kind: 'xy_washing', x: px(44), y: px(36.4), w: 28 }, { kind: 'xy_washing', x: px(38), y: px(42.4), w: 28 }, { kind: 'xy_washing', x: px(56), y: px(42.4), w: 24 });
add(tinShed(46.6, 45, 36, 1), tinShed(58.5, 33, 30, 2));
for (const [tx, ty, v] of [[36.4, 33.6, 0], [42.2, 33.6, 1], [48.6, 33.6, 0], [54.2, 33.6, 1], [37.6, 39.6, 1], [44.4, 39.6, 0], [50.6, 39.6, 1], [36.2, 45.6, 0]] as const) add({ kind: 'xm_herbPot', x: px(tx), y: px(ty), variant: v });
add({ kind: 'xm_chessTable', x: px(43), y: px(35.5), variant: 1, solid: solidBox(px(43) - 10, px(35.5) - 8, 20, 8) });
add(crowd(37, 35.6, 3, 24, 4, 0, 15), one(50.5, 39.8, 3, 0, 5), one(55, 35.6, 27, 1, 1, false), crowd(52, 47.6, 3, 24, 8, 0, 1));
add(cat(40.4, 33.9, 0, 1), cat(49, 33.05, 8 + 2, -1, 44), cat(45.6, 39.1, 3, 1), cat(56.8, 45.2, 9 + 4, 1));
add(benchSit(38, 54, 5), benchSit(54, 54, 6));
add({ kind: 'xy_pigeons2', x: px(50), y: px(43.6), w: 26, variant: 6 }, ground('xy_leaves', 56, 36.6, 7, 30), ground('xy_leaves', 48, 52.6, 8, 34), ground('xy_manhole', 46, 50.6));
add(ubike(40, 50.9, 48));

// --- Xinyi: shoppers, the busker's audience, food trucks, UBikes, flower beds, balloons --------------
add(crowd(70, 38.6, 6, 70, 12, 1, 2), crowd(78, 40.2, 4, 40, 8, 2, 3), crowd(67, 40.6, 4, 36, 5, 5, 4), crowd(65.5, 40, 3, 20, 6, 0, 5));
add(one(76, 42.6, 22, 0, 1), one(82, 46.4, 19, 2, 4), one(66, 46.6, 5, 3, 5), one(73.5, 48.2, 30, 1, 1), one(79, 55.4, 11, 0, 2));
add(foodTruck(66, 50.6, 0), queue(68.6, 50.4, 3, 1, 7), foodTruck(79, 50.6, 1), queue(81.6, 50.4, 2, 1, 8));
add(ubike(76, 47.6, 72));
add({ kind: 'xy_stroller', x: px(74), y: px(42.6), w: -1, variant: 1 });
add({ kind: 'ck_balloons', x: px(76), y: px(39) });
add(parterre(64.5, 55.5, 56, 16, 4), parterre(77.5, 55.5, 56, 16, 0));
add(benchSit(70, 53.1, 7), benchSit(81.5, 57.3, 8));
add({ kind: 'xy_pigeons2', x: px(69), y: px(48.6), w: 26, variant: 7 });
add(ground('xy_manhole', 71, 41.6), ground('xy_manhole', 77, 46.8), ground('xy_puddle', 67, 47.7, 0, 22), ground('xy_leaves', 62, 40.6, 9, 30), ground('xy_leaves', 80, 48.6, 10, 30));
add(streetTree(84, 50.4, 0), streetTree(70, 44.6, 1));
// Around the MRT exit: commuters pouring out, a UBike dock, a rooftop-style ad billboard, an ad panel.
add(crowd(64.6, 49.3, 4, 30, 8, 3, 6), one(63.2, 51.2, 14, 3, 5, false), one(68.5, 44.8, 28, 0, 1), ubike(66, 45.5, 40));
add({ kind: 'xm_billboard', x: px(72), y: px(46.6), w: 72, variant: 0, solid: solidBox(px(72) - 34, px(46.6) - 4, 68, 4) });
add({ kind: 'xm_billboard', x: px(63), y: px(58), w: 72, variant: 3 });

// --- Elephant Mountain: hikers on the stairs, photographers at the top, pavilions in the forest ------
add(one(87.5, 45.3, 25, 1, 6, false), one(88.4, 40.6, 9, 0, 6, false), one(92.6, 32.4, 17, 1, 6, false), one(92.4, 27.8, 31, 0, 6, false), one(89.5, 16.2, 13, 1, 6, false), one(85.5, 50.6, 2, 3, 6, false));
add(crowd(93, 24.3, 3, 24, 8, 4, 11));
add({ kind: 'xy_tripod', x: px(86.6), y: px(7.4), variant: 0 }, { kind: 'xy_tripod', x: px(88.6), y: px(6.2), variant: 1 });
add(crowd(88, 9.3, 4, 40, 6, 5, 12), one(90.2, 8.6, 29, 1, 4, false));
add({ kind: 'ck_pavilion', x: px(96.5), y: px(34) }, { kind: 'ck_pavilion', x: px(85.5), y: px(39) });
add(benchSit(89.8, 37.9, 9, false));
for (const [tx, ty] of [[86, 42], [90, 48], [94, 48], [86, 32], [95, 40], [91, 24], [97, 30], [94, 12], [87, 20], [96, 8], [98, 46]] as const) add({ kind: 'tk_fern', x: px(tx), y: px(ty), variant: (tx + ty) % 3 });

/** Night lights: street lamps, the station, the hall, 101's plaza, the shop signs, the trail. */
const lampLights = decor.filter((d) => d.kind === 'lamp').map((d): Light => ({ x: d.x, y: d.y - 30, r: 56, color: '255,224,150' }));
const lights: Light[] = [
  ...lampLights,
  { x: px(15), y: px(11), r: 150, color: '255,236,190' },
  { x: px(49), y: px(10), r: 150, color: '255,220,150' },
  { x: px(75), y: px(12), r: 140, color: '220,255,240' },
  { x: px(64), y: px(33), r: 110, color: '160,200,255' },
  { x: px(72), y: px(34), r: 90, color: '255,214,150' },
  { x: px(79), y: px(33), r: 90, color: '255,160,220' },
  ...[4, 9, 14, 19, 24, 28].map((tx): Light => ({ x: px(tx), y: px(33), r: 60, color: tx % 2 ? '255,140,110' : '150,220,255' })),
  { x: px(18), y: px(45), r: 90, color: '255,180,110' },
  { x: px(8), y: px(46), r: 70, color: '150,200,255' },
  { x: px(25), y: px(45), r: 50, color: '255,120,80' },
  { x: px(88), y: px(44), r: 40, color: '255,230,170' },
  { x: px(91), y: px(36), r: 40, color: '255,230,170' },
  { x: px(94), y: px(30), r: 40, color: '255,230,170' },
  { x: px(91), y: px(14), r: 40, color: '255,230,170' },
  { x: px(88), y: px(7), r: 70, color: '255,230,170' },
  ...[37, 43, 49, 55].map((x): Light => ({ x: px(x) + 8, y: px(38), r: 40, color: '255,210,140' })),
  // Street-life pass: neon shop signs on the arcade, the food trucks, the UBike kiosks, the pavilions.
  ...[[4.2, '255,90,90'], [7.6, '255,120,120'], [13.3, '255,220,120'], [17.4, '120,230,200'], [21.2, '120,220,140'], [25.6, '255,180,80'], [28.6, '255,210,120']].map(([tx, color]): Light => ({ x: px(tx as number), y: px(31.2), r: 34, color: color as string })),
  { x: px(80), y: px(18.8), r: 60, color: '255,210,160' },
  { x: px(66), y: px(48.6), r: 60, color: '255,210,160' },
  { x: px(79), y: px(48.6), r: 60, color: '255,190,140' },
  { x: px(24.5) - 32, y: px(41.6), r: 30, color: '255,200,120' },
  { x: px(76) - 44, y: px(46.2), r: 30, color: '255,200,120' },
  { x: px(96.5), y: px(31.5), r: 46, color: '255,220,160' },
  { x: px(85.5), y: px(36.5), r: 46, color: '255,220,160' },
  { x: px(61) + 8, y: px(45), r: 56, color: '200,230,255' },
  { x: px(66) - 28, y: px(44), r: 28, color: '255,200,120' },
  { x: px(72), y: px(44), r: 60, color: '255,120,220' },
  { x: px(63), y: px(55.6), r: 50, color: '255,255,255' },
];

export const taipei: MapDef = {
  id: 'taipei',
  w: b.w,
  h: b.h,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#e9dcc3', wallLight: '#f6ecd8', wainscot: '#8a5c3a', wallTop: '#3a2a20' },
  doors: [{ tiles: [[7, 47], [8, 47]], to: 'mrt', arrive: { x: px(2), y: px(4) + 8, facing: 'right' } }],
  decor,
  areas: [
    { key: 'xy_xiangshan', rect: { x: px(85), y: 0, w: px(15), h: px(H) } },
    { key: 'xy_viewpoint', rect: { x: px(84), y: px(3), w: px(8), h: px(9) } },
    { key: 'taipeiStation', rect: { x: 0, y: 0, w: px(30), h: px(22) } },
    { key: 'xy_sysHall', rect: { x: px(34), y: 0, w: px(29), h: px(22) } },
    { key: 'tower101', rect: { x: px(63), y: 0, w: px(22), h: px(22) } },
    { key: 'xy_avenue', rect: { x: 0, y: px(22), w: px(85), h: px(7) } },
    { key: 'xy_arcades', rect: { x: 0, y: px(29), w: px(30), h: px(13) } },
    { key: 'xy_stationFront', rect: { x: 0, y: px(42), w: px(30), h: px(18) } },
    { key: 'xy_village', rect: { x: px(34), y: px(29), w: px(26), h: px(31) } },
    { key: 'xy_xinyi', rect: { x: px(60), y: px(29), w: px(25), h: px(31) } },
    { key: 'taipei', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: {
    /** In front of Taipei Main Station, where the train from home arrives. */
    train: { x: px(16), y: px(15) + 8, facing: 'up' },
    /** Out of the Taipei 101 / World Trade Center MRT exit. */
    mrt: { x: px(61) + 8, y: px(47) + 8, facing: 'down' },
  },
};
